import { useToast } from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import {
  createUserWithProfile,
  fetchAuthUsers,
  isUserAdmin,
  sendVerificationEmail,
  supabase,
} from "../lib/supabase";
import type { Profile, UserRole } from "../types";

// Use Profile type directly for user management
export type User = Profile;

export interface CreateUserData {
  email: string;
  password: string;
  name: string;
}

export function useUserManagement() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentUserEmail, setCurrentUserEmail] = useState<string | undefined>(
    undefined
  );
  const [sendingVerification, setSendingVerification] = useState<string | null>(
    null
  );
  const toast = useToast();

  // Initialize user management data
  const initializeUserManagement = useCallback(async () => {
    setLoading(true);
    try {
      const {
        data: { user: authUser },
      } = await supabase.auth.getUser();
      setCurrentUserEmail(authUser?.email);

      const adminStatus = await isUserAdmin();
      setIsAdmin(adminStatus);

      if (adminStatus) {
        await fetchUsersData();
      }
    } catch (error: any) {
      toast({
        title: "Erro ao inicializar página de configurações",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Fetch users data
  const fetchUsersData = useCallback(async () => {
    try {
      const usersData = await fetchAuthUsers();
      setUsers(usersData || []);
    } catch (error: any) {
      toast({
        title: "Erro ao carregar usuários",
        description: error.message,
        status: "error",
        duration: 5000,
      });
    }
  }, [toast]);

  // Create new user
  const createUser = useCallback(
    async (userData: CreateUserData) => {
      if (!isAdmin) {
        throw new Error("Ação não permitida");
      }

      try {
        await createUserWithProfile(userData.email, userData.password, {
          name: userData.name,
        });

        toast({
          title: "Usuário criado com sucesso",
          description:
            "O usuário foi criado. Você pode enviar o e-mail de verificação quando necessário.",
          status: "success",
          duration: 5000,
        });

        await fetchUsersData();
      } catch (error: any) {
        toast({
          title: "Erro ao criar usuário",
          description:
            error.message || "Por favor, verifique os dados e tente novamente.",
          status: "error",
          duration: 5000,
        });
        throw error;
      }
    },
    [isAdmin, toast, fetchUsersData]
  );

  // Send verification email
  const handleSendVerificationEmail = useCallback(
    async (userId: string) => {
      setSendingVerification(userId);
      try {
        const result = await sendVerificationEmail(userId);

        toast({
          title: "E-mail de verificação gerado",
          description: `Link de verificação: ${result.verification_url}`,
          status: "success",
          duration: 10000,
          isClosable: true,
        });

        await fetchUsersData();
      } catch (error: any) {
        toast({
          title: "Erro ao enviar e-mail de verificação",
          description: error.message,
          status: "error",
          duration: 5000,
        });
      } finally {
        setSendingVerification(null);
      }
    },
    [toast, fetchUsersData]
  );

  // Update user role
  const updateUserRole = useCallback(
    async (userId: string, newRole: UserRole) => {
      if (!isAdmin) {
        toast({
          title: "Ação não permitida.",
          status: "error",
          duration: 3000,
        });
        return;
      }

      try {
        const { error } = await supabase
          .from("profiles")
          .update({ role: newRole })
          .eq("id", userId);

        if (error) throw error;

        toast({
          title: "Função atualizada com sucesso!",
          status: "success",
          duration: 3000,
        });

        await fetchUsersData();
      } catch (error: any) {
        toast({
          title: "Erro ao atualizar função",
          description: error.message,
          status: "error",
          duration: 5000,
        });
      }
    },
    [isAdmin, toast, fetchUsersData]
  );

  // Delete user
  const deleteUser = useCallback(
    async (userId: string) => {
      if (!isAdmin) {
        throw new Error("Ação não permitida");
      }

      try {
        const { error } = await supabase
          .from("profiles")
          .delete()
          .eq("id", userId);

        if (error) throw error;

        toast({
          title: "Usuário excluído com sucesso!",
          status: "success",
          duration: 3000,
        });

        await fetchUsersData();
      } catch (error: any) {
        toast({
          title: "Erro ao excluir usuário",
          description: error.message,
          status: "error",
          duration: 5000,
        });
        throw error;
      }
    },
    [isAdmin, toast, fetchUsersData]
  );

  // Initialize on mount
  useEffect(() => {
    initializeUserManagement();
  }, [initializeUserManagement]);

  return {
    // State
    users,
    loading,
    isAdmin,
    currentUserEmail,
    sendingVerification,

    // Actions
    createUser,
    updateUserRole,
    deleteUser,
    handleSendVerificationEmail,
    refreshUsers: fetchUsersData,
  };
}
